import { Component, computed, input } from '@angular/core';
import { UwIcon } from '../../ui/icon/uw-icon';
import { UwBadge } from '../../ui/badge/uw-badge';
import type { PricingEvidence } from '../../../core/models/evidence.model';
import { UwMoneyPipe } from '../../pipes/format.pipes';

@Component({
  selector: 'uw-pricing-evidence',
  standalone: true,
  imports: [UwIcon, UwBadge, UwMoneyPipe],
  template: `
    @if (pricing(); as p) {
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <p class="text-[12px] font-semibold text-primary">Technical rating vs. proposed</p>
          <uw-badge tone="success" subtle icon="check">Within pricing authority</uw-badge>
        </div>

        <div class="overflow-hidden rounded-lg border border-line">
          <table class="w-full border-collapse text-[11.5px]">
            <thead>
              <tr class="bg-surface-sunken/60">
                <th class="typo-label px-3 py-2 text-left">Coverage</th>
                <th class="typo-label px-3 py-2 text-right">Indicated</th>
                <th class="typo-label px-3 py-2 text-right">Proposed</th>
                <th class="typo-label px-3 py-2 text-right">Delta</th>
              </tr>
            </thead>
            <tbody>
              @for (row of p.rows; track row.coverage) {
                <tr class="border-t border-line">
                  <td class="px-3 py-2">
                    <p class="font-medium text-primary">{{ row.coverage }}</p>
                    <p class="text-[10px] text-text-muted">{{ row.detail }}</p>
                  </td>
                  <td class="px-3 py-2 text-right typo-mono text-secondary">{{ row.indicated | uwMoney }}</td>
                  <td class="px-3 py-2 text-right typo-mono font-semibold text-primary">{{ row.proposed | uwMoney }}</td>
                  <td class="px-3 py-2 text-right">
                    <span class="typo-mono font-semibold" [class]="deltaClass(row)">
                      {{ delta(row) }}
                    </span>
                  </td>
                </tr>
              }
              <tr class="border-t border-line bg-surface-sunken/40">
                <td class="px-3 py-2 text-[11.5px] font-semibold text-primary">Total</td>
                <td class="px-3 py-2 text-right typo-mono font-semibold text-primary">{{ total().indicated | uwMoney }}</td>
                <td class="px-3 py-2 text-right typo-mono font-bold text-primary">{{ total().proposed | uwMoney }}</td>
                <td class="px-3 py-2"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="flex items-start gap-1.5 rounded-lg bg-surface-sunken px-3 py-2 text-[10.5px] leading-snug text-text-muted">
          <uw-icon name="info" [size]="12" class="mt-0.5 shrink-0" />
          Proposed premium is 9.6% above expiring and 3.4% below technical indication — within the underwriter pricing authority.
        </p>
      </div>
    }
  `,
})
export class UwPricingEvidence {
  readonly pricing = input<PricingEvidence | null>(null);

  protected readonly total = computed(() => {
    const rows = this.pricing()?.rows ?? [];
    return {
      indicated: rows.reduce((s, r) => s + r.indicated, 0),
      proposed: rows.reduce((s, r) => s + r.proposed, 0),
    };
  });

  protected delta(row: { indicated: number; proposed: number }): string {
    const pct = ((row.proposed - row.indicated) / row.indicated) * 100;
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
  }

  protected deltaClass(row: { indicated: number; proposed: number }): string {
    const pct = ((row.proposed - row.indicated) / row.indicated) * 100;
    return pct <= 0 ? 'text-success' : 'text-warning';
  }
}
